import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Check, Mountain, Briefcase, Users, Heart, Dumbbell, User, Loader2, MapPin, ChevronUp, ArrowRight, Calendar, Home, Settings, Map, Shield, Laptop, Bike, Coffee } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Indian context images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1626621341517-bbf3d9990a23?w=1920&q=80",
  indianFamily: "https://static.prod-images.emergentagent.com/jobs/3eb0fe83-7002-462e-ad7f-2090ec45a2ac/images/8319affc19c9beba296344fd8c02fd062de6f9180c255390fdc7dfb668db1ccb.png",
  familyMotorHome: "https://static.prod-images.emergentagent.com/jobs/3eb0fe83-7002-462e-ad7f-2090ec45a2ac/images/5dfa1d149eb355b63b5b8841e8c62758b0066f540a72e88a1ffe28314a6ad7b7.png",
  himalayas: "https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=800&q=80",
  kerala: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=800&q=80",
  rajasthan: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=800&q=80",
  goa: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=800&q=80",
  northeast: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80",
  campingIndia: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80",
  camping: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800&q=80",
  ladakh: "https://images.pexels.com/photos/34998308/pexels-photo-34998308.jpeg?auto=compress&cs=tinysrgb&w=800",
  northeastIndia: "https://images.unsplash.com/photo-1639053035919-9e7f6fabb952?w=800&q=80",
  westernGhats: "https://images.pexels.com/photos/12311221/pexels-photo-12311221.jpeg?auto=compress&cs=tinysrgb&w=800",
  madhyaPradesh: "https://images.unsplash.com/photo-1589657429197-ecba47e3acd8?w=800&q=80",
  eastCoast: "https://images.pexels.com/photos/13308467/pexels-photo-13308467.jpeg?auto=compress&cs=tinysrgb&w=800",
  coachCaravan: "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&q=80",
  campervan: "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7?w=800&q=80",
  indianRoad: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80",
  familyCamping: "https://images.unsplash.com/photo-1496545672447-f699b503d270?w=800&q=80",
};

const VIDEOS = {
  hero: "https://videos.pexels.com/video-files/3571264/3571264-uhd_2560_1440_30fps.mp4",
  roadTrip: "https://videos.pexels.com/video-files/5752729/5752729-uhd_2560_1440_24fps.mp4",
};

// Floating CTA Button
const FloatingCTA = ({ activeIndex, onNavigate, onScrollTop }) => {
  const isVisible = activeIndex > 0 && activeIndex < 11;
  const showTop = activeIndex > 0;

  return (
    <>
      {/* WhatsApp button — left side, hidden on hero */}
      {activeIndex > 0 && (
        <a
          href="https://wa.me/916295484375"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="floating-whatsapp-btn"
          className="floating-whatsapp"
          title="Chat with us on WhatsApp"
        >
          <svg viewBox="0 0 32 32" width="24" height="24" fill="white">
            <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16.004c0 3.5 1.132 6.744 3.058 9.374L1.058 32l6.826-2.07C10.482 31.276 13.14 32 16.004 32 24.826 32 32 24.826 32 16.004 32 7.176 24.826 0 16.004 0zm9.32 22.616c-.39 1.1-1.932 2.014-3.166 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.974-7.826-6.81-8.064-7.124-.23-.314-1.932-2.574-1.932-4.908 0-2.334 1.222-3.48 1.656-3.956.39-.43 1.034-.644 1.65-.644.2 0 .378.01.54.018.474.02.712.048 1.024.792.39.934 1.34 3.268 1.458 3.506.118.238.236.556.078.87-.15.322-.282.464-.52.736-.238.272-.464.48-.702.774-.22.256-.468.53-.196 1.004.272.466 1.212 1.998 2.602 3.236 1.786 1.59 3.292 2.082 3.758 2.312.466.23.74.192 1.012-.116.28-.314 1.186-1.382 1.502-1.856.308-.474.624-.39 1.052-.234.432.152 2.762 1.302 3.234 1.54.474.236.788.356.906.554.116.198.116 1.148-.274 2.248z"/>
          </svg>
        </a>
      )}

      {showTop && (
        <button
          onClick={onScrollTop}
          data-testid="floating-top-btn"
          className="floating-top-btn"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
      <button
        onClick={onNavigate}
        data-testid="floating-cta"
        className={`floating-cta ${isVisible ? "visible" : ""}`}
      >
        <span>Express Founding Interest</span>
        <ArrowRight className="w-5 h-5" />
      </button>
    </>
  );
};

// Hero Section with Video Background
const HeroSection = () => {
  const videoRef = useRef(null);

  return (
    <section data-testid="hero-section" className="hero-section min-h-screen">
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        className="video-bg"
        poster={IMAGES.hero}
      >
        <source src={VIDEOS.hero} type="video/mp4" />
      </video>
      <div className="video-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 flex flex-col justify-center min-h-screen">
        <div className="flex items-center gap-4 mb-16 animate-fade-in-up opacity-0">
          <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
            <Mountain className="w-7 h-7 text-white" />
          </div>
          <div>
            <span className="font-serif text-2xl text-white block">The Caravan Network</span>
            <span className="text-[10px] text-[#F7C83F] tracking-wider block text-right">Powered by Pollen Dots</span>
          </div>
        </div>
        
        <h1 
          data-testid="hero-tagline"
          className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[1.05] text-white mb-8 animate-fade-in-up opacity-0 delay-100"
        >
          Outdoors.<br />
          Family.<br />
          Work. <span className="text-3xl md:text-4xl lg:text-5xl text-[#F7C83F] italic -ml-1">Together.</span>
        </h1>

        <p className="text-xl md:text-2xl text-white/70 max-w-xl mb-10 animate-fade-in-up opacity-0 delay-200">
          A new way to integrate nature, togetherness, and productivity into one seamless system.
        </p>

        <div className="flex flex-wrap gap-4 animate-fade-in-up opacity-0 delay-300">
          <a href="#interest-form" className="btn-primary text-lg flex items-center gap-2">
            Express Founding Interest
            <ArrowRight className="w-5 h-5" />
          </a>
          <a href="#why-different" className="btn-secondary text-lg">
            Learn More
          </a>
        </div>

      </div>
    </section>
  );
};

// Problem Section - Redesigned
const ProblemSection = () => {
  const compromises = [
    { left: "Nature", right: "Comfort" },
    { left: "Family time", right: "Work continuity" }, 
    { left: "Flexibility", right: "Structure" },
    { left: "Privacy", right: "Convenience" }
  ];

  return (
    <section data-testid="problem-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-7xl mx-auto">
        {/* Top intro */}
        <div className="max-w-3xl mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
            The Challenge
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-6 leading-tight">
            India Has Everything.<br />
            <span className="text-[#6B6560]">Except an easy way to enjoy it.</span>
          </h2>
          <p className="text-lg text-[#6B6560]">
            Mountains, coasts, forests, open roads. But spending meaningful time outdoors with family often requires compromise.
          </p>
        </div>

        {/* Main content grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left - Compromise cards + Image */}
          <div>
            <p className="text-sm font-semibold text-[#1A1816] mb-6">You choose between:</p>
            <div className="space-y-3 mb-8">
              {compromises.map((item, i) => (
                <div key={i} className="flex items-center bg-white rounded-xl p-4 border border-[#E0DCD7]">
                  <span className="flex-1 text-[#1A1816] font-medium">{item.left}</span>
                  <span className="px-4 py-1 rounded-full bg-[#F5F3F0] text-[#8A8580] text-sm">or</span>
                  <span className="flex-1 text-right text-[#1A1816] font-medium">{item.right}</span>
                </div>
              ))}
            </div>
            
            {/* Image */}
            <div className="section-image h-[300px]">
              <img 
                src={IMAGES.indianFamily}
                alt="Indian family enjoying outdoors"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Right - Solution positioning */}
          <div>
            {/* The equation changer */}
            <div className="bg-[#F5F3F0] rounded-2xl p-8 mb-6 border border-[#E0DCD7]">
              <p className="text-[#803D3B] font-semibold text-2xl mb-2">
                The Caravan Network changes that equation.
              </p>
              <p className="text-[#6B6560]">
                What if you didn't have to choose?
              </p>
            </div>

            {/* Main solution card */}
            <div className="bg-[#803D3B] rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                  <Mountain className="w-6 h-6 text-[#F7C83F]" />
                </div>
                <span className="text-[#F7C83F] text-sm font-semibold tracking-wider uppercase">The Solution</span>
              </div>
              
              <h3 className="font-serif text-3xl mb-4 leading-tight">
                A New Way to Travel as a Family
              </h3>
              
              <p className="text-white/80 mb-6">
                It enables structured, repeat, service-led outdoor travel — without operational burden.
              </p>

              <ul className="space-y-3">
                {["Nature AND comfort", "Family time AND work continuity", "Flexibility AND structure"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#F7C83F]" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why This Is Different Section
const WhyDifferentSection = () => {
  const notThis = [
    { text: "Not rental", desc: "No per-trip booking uncertainty" },
    { text: "Not vehicle ownership", desc: "No maintenance burden" },
    { text: "Not a marketplace", desc: "No variable quality" },
  ];

  const thisIs = [
    { text: "Structured long-term access", icon: Calendar },
    { text: "Managed service layer", icon: Settings },
    { text: "Permanent support infrastructure", icon: Shield },
  ];

  return (
    <section id="why-different" data-testid="why-different-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5F3F0]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-4">
            Why This Is Different
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
          <div className="bg-white rounded-2xl p-8 border border-[#E0DCD7] shadow-sm">
            <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-[#B8B3AD] mb-6">
              This is not
            </h3>
            <ul className="space-y-5">
              {notThis.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="w-6 h-6 rounded-full bg-[#F5F3F0] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-[#B8B3AD]" />
                  </span>
                  <div>
                    <span className="text-[#1A1816] font-semibold block">{item.text}</span>
                    <span className="text-[#8A8580] text-sm">{item.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#803D3B] rounded-2xl p-8 text-white">
            <h3 className="text-sm font-semibold tracking-[0.15em] uppercase text-[#F7C83F] mb-6">
              This is
            </h3>
            <ul className="space-y-5">
              {thisIs.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#F7C83F]" />
                  </span>
                  <span className="text-white/90 font-medium pt-2">{item.text}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-white/70 text-sm leading-relaxed">
                A closed ecosystem where access is structured, service is guaranteed, and your experience remains consistent — every time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// The System - 3-Step Explanation
const SystemSection = () => {
  const steps = [
    {
      number: "01",
      title: "Structured Access Allocation",
      description: "Members receive defined access units based on predicted annual usage. No daily negotiation. No availability anxiety.",
      icon: Calendar,
      details: ["Annual usage allocation", "Priority hub selection", "Predictable scheduling"]
    },
    {
      number: "02", 
      title: "Managed Service & Reset",
      description: "Every caravan is reset to identical standards before each handover. Clean. Checked. Ready.",
      icon: Settings,
      details: ["Professional cleaning protocols", "Mechanical inspection", "Consumables restocked"]
    },
    {
      number: "03",
      title: "National Hub Network",
      description: "Collect from one hub. Return to any hub. The network handles logistics — you handle living.",
      icon: Map,
      details: ["Multi-city hub presence", "Flexible pickup/return", "24/7 support access"]
    }
  ];

  return (
    <section data-testid="system-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-4">
            How The System Works
          </h2>
          <p className="text-[#6B6560] text-lg max-w-2xl mx-auto">
            Three pillars that make structured outdoor living possible.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {steps.map((step, i) => (
            <div key={i} className="system-card group">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-4xl font-serif text-[#E0DCD7] group-hover:text-[#D4654A] transition-colors">
                  {step.number}
                </span>
                <div className="w-12 h-12 rounded-xl bg-[#803D3B]/5 flex items-center justify-center group-hover:bg-[#803D3B] transition-colors">
                  <step.icon className="w-6 h-6 text-[#803D3B] group-hover:text-white transition-colors" />
                </div>
              </div>
              
              <h3 className="font-serif text-xl text-[#1A1816] mb-3">
                {step.title}
              </h3>
              
              <p className="text-[#6B6560] text-sm mb-6 leading-relaxed">
                {step.description}
              </p>

              <ul className="space-y-2">
                {step.details.map((detail, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm text-[#8A8580]">
                    <span className="w-1 h-1 rounded-full bg-[#F7C83F]" />
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 system-diagram">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="diagram-node">
              <Home className="w-5 h-5" />
              <span>Your City</span>
            </div>
            <ArrowRight className="w-5 h-5 text-[#F7C83F] hidden md:block" />
            <div className="diagram-node active">
              <Mountain className="w-5 h-5" />
              <span>Hub Pickup</span>
            </div>
            <ArrowRight className="w-5 h-5 text-[#F7C83F] hidden md:block" />
            <div className="diagram-node">
              <Map className="w-5 h-5" />
              <span>Your Journey</span>
            </div>
            <ArrowRight className="w-5 h-5 text-[#F7C83F] hidden md:block" />
            <div className="diagram-node">
              <Settings className="w-5 h-5" />
              <span>Any Hub Return</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What This Enables Section - Redesigned
const EnablesSection = () => {
  const enables = [
    { text: "Weekend departures without hotel coordination", icon: Calendar },
    { text: "Outdoor immersion without infrastructure anxiety", icon: Mountain },
    { text: "Work continuity without sacrificing family time", icon: Laptop },
    { text: "Familiar living space across regions", icon: Home },
    { text: "Predictable, hygienic, managed travel", icon: Check }
  ];

  return (
    <section id="what-enables" data-testid="enables-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5F3F0]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-2xl mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
            The Possibility
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] leading-tight">
            What This Enables
          </h2>
        </div>

        {/* Quote — text emphasis, no box */}
        <div className="mb-16 max-w-4xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#8A8580] leading-relaxed">
            This is not about escaping life.
          </p>
          <div className="w-16 h-[2px] bg-[#F7C83F] mx-auto my-5" />
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl leading-relaxed">
            <span className="text-[#1A1816] font-semibold">It is about integrating</span>{" "}
            <span className="text-[#803D3B] font-semibold italic">outdoors, family and work</span>{" "}
            <span className="text-[#1A1816] font-semibold">into one system.</span>
          </p>
        </div>

        {/* Benefits grid with images */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left column - stacked images */}
          <div className="space-y-4">
            <div className="image-card h-48 relative group overflow-hidden rounded-2xl">
              <img src={IMAGES.himalayas} alt="Himalayan mountains" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <span className="absolute bottom-4 left-4 text-white font-medium">Himalayas</span>
            </div>
            <div className="image-card h-48 relative group overflow-hidden rounded-2xl">
              <img src={IMAGES.goa} alt="Goa beach" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <span className="absolute bottom-4 left-4 text-white font-medium">Goa</span>
            </div>
          </div>

          {/* Middle column - benefits list */}
          <div className="bg-white rounded-2xl p-8 border border-[#E0DCD7] flex flex-col justify-center">
            <ul className="space-y-5">
              {enables.map((item, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <span className="w-10 h-10 rounded-xl bg-[#803D3B]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#803D3B] transition-colors">
                    <item.icon className="w-5 h-5 text-[#803D3B] group-hover:text-white transition-colors" />
                  </span>
                  <span className="text-[#1A1816] pt-2">{item.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right column - stacked images */}
          <div className="space-y-4">
            <div className="image-card h-48 relative group overflow-hidden rounded-2xl">
              <img src={IMAGES.rajasthan} alt="Rajasthan desert" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <span className="absolute bottom-4 left-4 text-white font-medium">Rajasthan</span>
            </div>
            <div className="image-card h-48 relative group overflow-hidden rounded-2xl">
              <img src={IMAGES.kerala} alt="Kerala backwaters" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <span className="absolute bottom-4 left-4 text-white font-medium">Kerala</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Who Uses This - Real Scenarios
const ScenariosSection = () => {
  const scenarios = [
    {
      icon: Laptop,
      title: "Working Parents",
      scenario: "Maintain professional commitments while the family travels. Remote work from the mountains. Calls between activities.",
      outcome: "Work continuity without sacrificing family time.",
    },
    {
      icon: Briefcase,
      title: "Entrepreneurs & Founders",
      scenario: "Stay connected with reliable internet. Strategic thinking in new environments.",
      outcome: "Business continuity with mental reset.",
    },
    {
      icon: Bike,
      title: "Health-Conscious Travellers",
      scenario: "Carry your cycles. Cook your meals. Maintain your fitness routine.",
      outcome: "Your lifestyle, mobile.",
    },
    {
      icon: Heart,
      title: "Couples Seeking Mobility",
      scenario: "Weekend escapes without booking hassle. Same familiar space, different destinations.",
      outcome: "Spontaneous travel, zero friction.",
    },
    {
      icon: Users,
      title: "Senior Citizens",
      scenario: "Predictable layouts. Familiar systems. Managed support available.",
      outcome: "Comfortable exploration at your pace.",
    },
    {
      icon: Coffee,
      title: "Remote Workers",
      scenario: "Change your view, not your productivity. Dedicated workspace. Reliable connectivity.",
      outcome: "The office moves with you.",
    }
  ];

  return (
    <section data-testid="scenarios-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-3">
            Who This Is Designed For
          </h2>
          <div className="w-12 h-[2px] bg-[#F7C83F] mx-auto" />
        </div>

        {/* Top row — 2 featured cards */}
        <div className="grid md:grid-cols-2 gap-5 mb-5">
          {scenarios.slice(0, 2).map((item, i) => (
            <div key={i} className="group relative bg-white rounded-2xl p-7 border border-[#E0DCD7] hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-[#803D3B] rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-[#803D3B]/8 flex items-center justify-center flex-shrink-0 group-hover:bg-[#803D3B] transition-colors duration-300">
                  <item.icon className="w-7 h-7 text-[#803D3B] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-serif text-xl text-[#1A1816]">{item.title}</h3>
              </div>
              <p className="text-[#6B6560] text-sm leading-relaxed mb-5">{item.scenario}</p>
              <p className="text-[#803D3B] text-sm font-semibold flex items-center gap-2">
                <span className="w-5 h-[2px] bg-[#F7C83F] inline-block" />
                {item.outcome}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom rows — 4 compact cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {scenarios.slice(2).map((item, i) => (
            <div key={i} className="group relative bg-white rounded-2xl p-6 border border-[#E0DCD7] hover:shadow-lg transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-[#803D3B] rounded-l-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="w-12 h-12 rounded-xl bg-[#803D3B]/8 flex items-center justify-center mb-4 group-hover:bg-[#803D3B] transition-colors duration-300">
                <item.icon className="w-6 h-6 text-[#803D3B] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-serif text-lg text-[#1A1816] mb-2">{item.title}</h3>
              <p className="text-[#6B6560] text-xs leading-relaxed mb-4">{item.scenario}</p>
              <p className="text-[#803D3B] text-xs font-semibold flex items-center gap-2">
                <span className="w-4 h-[2px] bg-[#F7C83F] inline-block" />
                {item.outcome}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Destinations Section
const DestinationsSection = () => {
  const regions = [
    { name: "Ladakh", image: IMAGES.ladakh, desc: "High-altitude serenity" },
    { name: "Northeast", image: IMAGES.northeastIndia, desc: "Untouched landscapes" },
    { name: "Western Ghats", image: IMAGES.westernGhats, desc: "Lush green escapes" },
    { name: "Madhya Pradesh", image: IMAGES.madhyaPradesh, desc: "Wildlife & heritage" },
    { name: "Kerala", image: IMAGES.kerala, desc: "Backwater calm" },
    { name: "East Coast", image: IMAGES.eastCoast, desc: "Coastal horizons" },
  ];

  return (
    <section data-testid="destinations-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5F3F0]">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-4">
            One System. Many Destinations.
          </h2>
          <p className="text-[#6B6560] text-lg max-w-2xl mx-auto">
            The same familiar caravan. The same managed service. Across India's most beautiful regions.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {regions.map((region, i) => (
            <div key={i} className="landscape-card group">
              <img src={region.image} alt={region.name} className="w-full h-full object-cover" />
              <div className="landscape-overlay">
                <h3 className="font-serif text-xl text-white">{region.name}</h3>
                <p className="text-white/70 text-sm">{region.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Indian Lifestyle Section
const IndianLifestyleSection = () => {
  const notAccustomed = [
    "Managing waste systems",
    "Handling electrical load calculations",
    "Performing mechanical diagnostics",
    "Living without structured support"
  ];

  const serviceEnsures = [
    "Clean resets",
    "Maintained utilities",
    "Managed handovers",
    "Support throughout usage"
  ];

  return (
    <section data-testid="indian-lifestyle-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="section-image h-[400px] lg:h-[500px] order-2 lg:order-1">
            <img 
              src={IMAGES.familyMotorHome}
              alt="Family with children enjoying coach caravan outdoors"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="order-1 lg:order-2">
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
              Built for India
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-10 leading-tight">
              Designed for the Indian Lifestyle
            </h2>
            
            <p className="text-lg text-[#6B6560] mb-6">
              Indian families are service-dependent by nature.
            </p>
            
            <p className="text-[#6B6560] mb-4">We are not accustomed to:</p>
            
            <ul className="space-y-2 text-[#6B6560] mb-10">
              {notAccustomed.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6B6560]" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-[#803D3B] font-semibold text-lg mb-6">
              The Caravan Network is built for this reality.
            </p>
            
            <p className="text-[#6B6560] mb-4">A dedicated service layer ensures:</p>
            
            <ul className="space-y-3 text-[#6B6560] mb-8">
              {serviceEnsures.map((item, i) => (
                <li key={i} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#D4654A]/10 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-[#D4654A]" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-[#1A1816] font-medium italic text-lg">
              The experience remains comfortable, structured and predictable.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Closed Community Section
const ClosedCommunitySection = () => {
  const ensures = [
    "Hygiene discipline",
    "Asset care",
    "Behaviour alignment",
    "Long-term continuity"
  ];

  return (
    <section data-testid="community-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5F3F0]">
      <div className="max-w-4xl mx-auto text-center">
        <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
          Governed Access
        </span>
        <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-10 leading-tight">
          A Closed Community Model
        </h2>
        
        <p className="text-xl text-[#6B6560] mb-8">
          Access is limited to a defined community of participating families.
        </p>
        
        <p className="text-[#6B6560] mb-6">This ensures:</p>
        
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {ensures.map((item, i) => (
            <span 
              key={i} 
              className="px-6 py-3 rounded-full bg-white border border-[#E0DCD7] text-[#1A1816] font-medium shadow-sm"
            >
              {item}
            </span>
          ))}
        </div>

        <div className="text-[#6B6560] space-y-2">
          <p>This is not open marketplace usage.</p>
          <p className="text-[#803D3B] font-semibold text-xl">
            It is a structured network built on repeat participation.
          </p>
        </div>
      </div>
    </section>
  );
};

// Benefits Section - Redesigned
const BenefitsSection = () => {
  const benefits = [
    { text: "Expanding infrastructure footprint", icon: Map },
    { text: "Access to additional regions", icon: Mountain },
    { text: "Loyalty advantages in future cycles", icon: Heart },
    { text: "Eligibility for self-drive access in advanced participation stages", icon: ArrowRight }
  ];

  return (
    <section data-testid="benefits-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left - Content */}
          <div>
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
              Long-Term Value
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-6 leading-tight">
              Long-Term Participation Benefits
            </h2>
            
            <p className="text-lg text-[#6B6560] mb-8">
              Participants may choose one or multiple access units based on predicted annual usage.
            </p>

            <p className="text-[#1A1816] font-medium mb-6">Over time, benefits increase through:</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-5 border border-[#E0DCD7] flex items-start gap-4">
                  <span className="w-10 h-10 rounded-lg bg-[#803D3B]/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-[#803D3B]" />
                  </span>
                  <span className="text-[#1A1816] text-sm pt-2">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Value card */}
          <div className="bg-[#803D3B] rounded-2xl p-8 text-white lg:sticky lg:top-8">
            <div className="mb-8">
              <span className="text-[#F7C83F] text-sm font-semibold">Residual Value</span>
              <h3 className="font-serif text-2xl mt-2">Your participation has lasting value</h3>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-[#F7C83F] font-bold">1</span>
                </span>
                <div>
                  <p className="text-white/90 font-medium">Refund Option</p>
                  <p className="text-white/60 text-sm">Residual value is refundable at the end of the term</p>
                </div>
              </div>
              
              <div className="border-t border-white/10 pt-6">
                <div className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#F7C83F] flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-[#803D3B] font-bold">2</span>
                  </span>
                  <div>
                    <p className="text-white font-medium">Roll Over Option</p>
                    <p className="text-white/70 text-sm">Roll over into the next cycle and unlock additional benefits and self-drive eligibility</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What Becomes Possible Section - Redesigned
const PossibleSection = () => {
  const weekendFlow = [
    { day: "Friday", time: "Evening", action: "Leave the city" },
    { day: "Saturday", time: "Morning", action: "Wake up at the coast" },
    { day: "Sunday", time: "Night", action: "Return drive" },
    { day: "Monday", time: "Morning", action: "Attend work" },
  ];

  return (
    <section data-testid="possible-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#F5F3F0]">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - Timeline */}
          <div>
            <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
              A Typical Weekend
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-8 leading-tight">
              What Becomes Possible
            </h2>

            <div className="space-y-0">
              {weekendFlow.map((item, i) => (
                <div key={i} className="flex items-stretch">
                  {/* Timeline line */}
                  <div className="flex flex-col items-center mr-6">
                    <div className={`w-4 h-4 rounded-full ${i === 3 ? 'bg-[#803D3B]' : 'bg-[#D4654A]'} flex-shrink-0 z-10`} />
                    {i < 3 && <div className="w-0.5 h-full bg-[#E0DCD7] -mt-0" />}
                  </div>
                  {/* Content */}
                  <div className={`pb-8 ${i === 3 ? 'pb-0' : ''}`}>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-sm font-semibold text-[#803D3B]">{item.day}</span>
                      <span className="text-xs text-[#8A8580] bg-[#E8E4DF] px-2 py-0.5 rounded">{item.time}</span>
                    </div>
                    <p className={`text-lg ${i === 3 ? 'text-[#803D3B] font-semibold' : 'text-[#1A1816]'}`}>
                      {item.action}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Benefits */}
          <div className="bg-white rounded-2xl p-8 border border-[#E0DCD7]">
            <h3 className="font-serif text-2xl text-[#1A1816] mb-6">Without the usual friction</h3>
            
            <ul className="space-y-4 mb-8">
              {[
                "Re-planning everything each time",
                "Negotiating hotels each time",
                "Sacrificing routine or work",
                "Operational anxiety"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-[#6B6560]">
                  <span className="w-5 h-5 rounded-full border border-[#D4654A]/30 flex items-center justify-center flex-shrink-0">
                    <span className="w-2 h-2 rounded-full bg-[#D4654A]" />
                  </span>
                  No {item.toLowerCase()}
                </li>
              ))}
            </ul>

            <div className="pt-6 border-t border-[#E0DCD7]">
              <p className="font-serif text-2xl text-[#1A1816] leading-relaxed">
                Outdoors. Family. Work.<br />
                <span className="text-[#803D3B]">In one system.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Interest Form Section
const InterestFormSection = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    pincode: "",
    city: "",
    district: "",
    state: "",
    occupation: "",
    householdType: "",
    annualSpend: "",
    preferredRegions: [],
    tripsPerYear: "",
    replaceHotels: "",
    email: "",
    phone: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const [pincodeError, setPincodeError] = useState("");

  const occupations = ["Corporate Professional", "Founder / Entrepreneur", "Business Owner", "Other"];
  const householdTypes = ["Couple", "Family with Children", "Multi-generational", "Individual"];
  const spendRanges = ["Rs. 2-3 Lakhs", "Rs. 3-5 Lakhs", "Rs. 5 Lakhs+"];
  const regions = ["Himachal / Ladakh", "Uttarakhand", "Rajasthan", "Goa / Maharashtra", "Karnataka / South", "Northeast", "Other"];
  const tripsOptions = ["2-3", "4-6", "6+"];
  const replaceOptions = ["Yes", "Possibly", "Not sure"];

  const fetchPincodeData = async (pincode) => {
    if (pincode.length !== 6) {
      setPincodeError("");
      setFormData(prev => ({ ...prev, city: "", district: "", state: "" }));
      return;
    }
    setPincodeLoading(true);
    setPincodeError("");
    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await response.json();
      if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
        const postOffice = data[0].PostOffice[0];
        setFormData(prev => ({
          ...prev,
          city: postOffice.Block || postOffice.Name || "",
          district: postOffice.District || "",
          state: postOffice.State || ""
        }));
      } else {
        setPincodeError("Invalid pincode. Please check and try again.");
        setFormData(prev => ({ ...prev, city: "", district: "", state: "" }));
      }
    } catch (error) {
      setPincodeError("Unable to fetch location. Please enter manually.");
    } finally {
      setPincodeLoading(false);
    }
  };

  const handlePincodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setFormData({ ...formData, pincode: value });
    if (value.length === 6) fetchPincodeData(value);
  };

  const handleRegionToggle = (region) => {
    setFormData(prev => ({
      ...prev,
      preferredRegions: prev.preferredRegions.includes(region)
        ? prev.preferredRegions.filter(r => r !== region)
        : [...prev.preferredRegions, region]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/interest`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error("Error:", error);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="interest-form" data-testid="interest-success" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-24 h-24 rounded-full bg-[#803D3B] flex items-center justify-center mx-auto mb-8">
            <Check className="w-12 h-12 text-white" />
          </div>
          <h2 className="font-serif text-4xl text-[#1A1816] mb-4">
            Thank You for Your Interest
          </h2>
          <p className="text-xl text-[#6B6560]">
            We have received your details. Our team will review your profile and reach out as we roll out access in your preferred regions.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="interest-form" data-testid="interest-form-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#FAF9F7]">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D4654A] mb-4 block">
            Join Us
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-[#1A1816] mb-6 leading-tight">
            Express Founding Interest
          </h2>
        </div>
        
        <div className="content-card mb-8">
          <div className="space-y-4 text-[#6B6560]">
            <p>We are currently gathering structured interest from families and individuals who believe this model fits their lifestyle.</p>
            <p className="font-semibold text-[#1A1816]">This is not a booking form.</p>
            <p>It helps us understand:</p>
            <ul className="space-y-1 pl-4 text-sm">
              <li>• Usage expectations</li>
              <li>• Preferred regions</li>
              <li>• Travel frequency</li>
              <li>• Lifestyle alignment</li>
            </ul>
            <p>Access will be rolled out in select hubs in phased stages.</p>
            <p className="italic text-[#803D3B] font-medium">
              If this resonates with how you want to travel in the coming years — share your details below.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-2">Full Name *</label>
            <input type="text" data-testid="input-fullname" required value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="form-input" placeholder="Enter your full name" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-2">Pincode of Current Residence *</label>
            <div className="relative">
              <input type="text" data-testid="input-pincode" required value={formData.pincode}
                onChange={handlePincodeChange} className="form-input pr-12"
                placeholder="Enter 6-digit pincode" maxLength={6} />
              {pincodeLoading && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-[#803D3B] animate-spin" />
                </div>
              )}
              {!pincodeLoading && formData.city && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
              )}
            </div>
            {pincodeError && <p className="text-red-500 text-sm mt-2">{pincodeError}</p>}
          </div>

          {(formData.city || formData.district || formData.state) && (
            <div className="grid md:grid-cols-3 gap-4 p-4 bg-[#803D3B]/5 rounded-xl border border-[#803D3B]/10">
              <div>
                <label className="block text-xs font-medium text-[#6B6560] mb-1">City/Town/Village</label>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#803D3B]" />
                  <span className="text-[#1A1816] font-medium">{formData.city || "-"}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B6560] mb-1">District</label>
                <span className="text-[#1A1816] font-medium">{formData.district || "-"}</span>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B6560] mb-1">State</label>
                <span className="text-[#1A1816] font-medium">{formData.state || "-"}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">Occupation *</label>
            <div className="grid grid-cols-2 gap-3">
              {occupations.map((occ) => (
                <button key={occ} type="button"
                  data-testid={`occupation-${occ.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setFormData({...formData, occupation: occ})}
                  className={`px-4 py-3.5 rounded-xl border-2 text-left transition-all font-medium ${
                    formData.occupation === occ 
                      ? 'border-[#803D3B] bg-[#803D3B] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#803D3B]/50'
                  }`}>
                  {occ}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">Household Type *</label>
            <div className="grid grid-cols-2 gap-3">
              {householdTypes.map((type) => (
                <button key={type} type="button"
                  data-testid={`household-${type.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setFormData({...formData, householdType: type})}
                  className={`px-4 py-3.5 rounded-xl border-2 text-left transition-all font-medium ${
                    formData.householdType === type 
                      ? 'border-[#803D3B] bg-[#803D3B] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#803D3B]/50'
                  }`}>
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">Approximate Annual Domestic Travel Spend *</label>
            <div className="grid grid-cols-3 gap-3">
              {spendRanges.map((range) => (
                <button key={range} type="button"
                  data-testid={`spend-${range.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setFormData({...formData, annualSpend: range})}
                  className={`px-4 py-3.5 rounded-xl border-2 text-center transition-all font-medium ${
                    formData.annualSpend === range 
                      ? 'border-[#D4654A] bg-[#D4654A] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#D4654A]/50'
                  }`}>
                  {range}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">Preferred Regions (Select Multiple) *</label>
            <div className="flex flex-wrap gap-3">
              {regions.map((region) => (
                <button key={region} type="button"
                  data-testid={`region-${region.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleRegionToggle(region)}
                  className={`px-5 py-2.5 rounded-full border-2 transition-all font-medium ${
                    formData.preferredRegions.includes(region)
                      ? 'border-[#F7C83F] bg-[#F7C83F] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#F7C83F]/50'
                  }`}>
                  {region}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">How many short trips per year do you typically take? *</label>
            <div className="grid grid-cols-3 gap-3">
              {tripsOptions.map((option) => (
                <button key={option} type="button"
                  data-testid={`trips-${option}`}
                  onClick={() => setFormData({...formData, tripsPerYear: option})}
                  className={`px-4 py-3.5 rounded-xl border-2 text-center transition-all font-medium ${
                    formData.tripsPerYear === option 
                      ? 'border-[#803D3B] bg-[#803D3B] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#803D3B]/50'
                  }`}>
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-3">Would structured, managed caravan access replace part of your current hotel stays? *</label>
            <div className="grid grid-cols-3 gap-3">
              {replaceOptions.map((option) => (
                <button key={option} type="button"
                  data-testid={`replace-${option.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setFormData({...formData, replaceHotels: option})}
                  className={`px-4 py-3.5 rounded-xl border-2 text-center transition-all font-medium ${
                    formData.replaceHotels === option 
                      ? 'border-[#803D3B] bg-[#803D3B] text-white' 
                      : 'border-[#E0DCD7] text-[#6B6560] hover:border-[#803D3B]/50'
                  }`}>
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-2">Email *</label>
            <input type="email" data-testid="input-email" required value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="form-input" placeholder="your@email.com" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1A1816] mb-2">Phone Number *</label>
            <input type="tel" data-testid="input-phone" required value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="form-input" placeholder="+91 XXXXX XXXXX" />
          </div>

          <button type="submit" data-testid="submit-interest"
            disabled={loading || !formData.pincode || !formData.city}
            className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3">
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Express Founding Interest
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer data-testid="footer" className="py-12 px-6 md:px-12 lg:px-16 bg-[#1A1816]">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Mountain className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-lg text-white">The Caravan Network</span>
          </div>
          <p className="text-white/50 text-sm">
            © 2024 Pollen Dots. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Section configuration
const SECTION_CONFIG = [
  { id: 'hero', name: 'Home', bg: 'transparent', isDark: true },
  { id: 'problem', name: 'Challenge', bg: '#FAF9F7', isDark: false },
  { id: 'why-different', name: 'Different', bg: '#F5F3F0', isDark: false },
  { id: 'system', name: 'System', bg: '#FAF9F7', isDark: false },
  { id: 'what-enables', name: 'Enables', bg: '#F5F3F0', isDark: false },
  { id: 'scenarios', name: 'Scenarios', bg: '#FAF9F7', isDark: false },
  { id: 'destinations', name: 'Destinations', bg: '#F5F3F0', isDark: false },
  { id: 'lifestyle', name: 'Lifestyle', bg: '#FAF9F7', isDark: false },
  { id: 'community', name: 'Community', bg: '#F5F3F0', isDark: false },
  { id: 'benefits', name: 'Benefits', bg: '#FAF9F7', isDark: false },
  { id: 'possible', name: 'Possible', bg: '#F5F3F0', isDark: false },
  { id: 'interest-form', name: 'Interest', bg: '#FAF9F7', isDark: false },
  { id: 'footer', name: 'Footer', bg: '#1A1816', isDark: true },
];

// Main App
function App() {
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRefs = useRef([]);
  const isTransitioning = useRef(false);
  const canNavUp = useRef(true);
  const canNavDown = useRef(true);
  const touchStartY = useRef(0);
  const touchBoundary = useRef({ up: true, down: true });

  const totalSections = SECTION_CONFIG.length;
  const isDarkSection = SECTION_CONFIG[activeIndex]?.isDark;

  const goTo = useCallback((index) => {
    if (index < 0 || index >= totalSections || isTransitioning.current) return;
    isTransitioning.current = true;
    setActiveIndex(index);
    const el = sectionRefs.current[index];
    if (el) el.scrollTop = 0;
    setTimeout(() => { isTransitioning.current = false; }, 800);
  }, [totalSections]);

  // Track active section scroll bounds
  useEffect(() => {
    const el = sectionRefs.current[activeIndex];
    if (!el) return;
    const check = () => {
      const noScroll = el.scrollHeight <= el.clientHeight + 2;
      canNavUp.current = el.scrollTop < 2 || noScroll;
      canNavDown.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 2 || noScroll;
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    return () => el.removeEventListener('scroll', check);
  }, [activeIndex]);

  // Wheel navigation
  useEffect(() => {
    let accDelta = 0;
    let lastTime = 0;
    const handler = (e) => {
      if (isTransitioning.current) { e.preventDefault(); return; }
      if (e.deltaY > 0 && !canNavDown.current) { accDelta = 0; return; }
      if (e.deltaY < 0 && !canNavUp.current) { accDelta = 0; return; }
      e.preventDefault();
      const now = Date.now();
      if (now - lastTime > 200) accDelta = 0;
      lastTime = now;
      accDelta += e.deltaY;
      if (accDelta > 50) { accDelta = 0; goTo(activeIndex + 1); }
      else if (accDelta < -50) { accDelta = 0; goTo(activeIndex - 1); }
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [activeIndex, goTo]);

  // Touch navigation
  useEffect(() => {
    const start = (e) => {
      touchStartY.current = e.touches[0].clientY;
      const el = sectionRefs.current[activeIndex];
      if (el) {
        const noScroll = el.scrollHeight <= el.clientHeight + 2;
        touchBoundary.current = {
          up: el.scrollTop < 2 || noScroll,
          down: (el.scrollHeight - el.scrollTop - el.clientHeight) < 2 || noScroll
        };
      }
    };
    const end = (e) => {
      if (isTransitioning.current) return;
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 50) return;
      if (diff > 0 && touchBoundary.current.down) goTo(activeIndex + 1);
      else if (diff < 0 && touchBoundary.current.up) goTo(activeIndex - 1);
    };
    window.addEventListener('touchstart', start, { passive: true });
    window.addEventListener('touchend', end, { passive: true });
    return () => {
      window.removeEventListener('touchstart', start);
      window.removeEventListener('touchend', end);
    };
  }, [activeIndex, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); goTo(activeIndex + 1); }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') { e.preventDefault(); goTo(activeIndex - 1); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIndex, goTo]);

  // Hash link interception
  useEffect(() => {
    const hashMap = {};
    SECTION_CONFIG.forEach((s, i) => { hashMap[`#${s.id}`] = i; });
    const handleClick = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute('href');
      if (hashMap[hash] !== undefined) {
        e.preventDefault();
        goTo(hashMap[hash]);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [goTo]);

  const sections = [
    <HeroSection key="hero" />,
    <ProblemSection key="problem" />,
    <WhyDifferentSection key="why-different" />,
    <SystemSection key="system" />,
    <EnablesSection key="enables" />,
    <ScenariosSection key="scenarios" />,
    <DestinationsSection key="destinations" />,
    <IndianLifestyleSection key="lifestyle" />,
    <ClosedCommunitySection key="community" />,
    <BenefitsSection key="benefits" />,
    <PossibleSection key="possible" />,
    <InterestFormSection key="interest" />,
    <Footer key="footer" />,
  ];

  return (
    <div className="fullpage-container" data-testid="fullpage-container">
      <FloatingCTA activeIndex={activeIndex} onNavigate={() => goTo(11)} onScrollTop={() => goTo(0)} />
      {sections.map((section, i) => (
        <div
          key={SECTION_CONFIG[i].id}
          ref={el => sectionRefs.current[i] = el}
          className={`fullpage-section ${i === activeIndex ? 'fp-active' : i < activeIndex ? 'fp-above' : 'fp-below'}`}
          style={{ background: SECTION_CONFIG[i].bg }}
          data-testid={`section-${SECTION_CONFIG[i].id}`}
        >
          {section}
        </div>
      ))}
      <nav className={`fullpage-nav ${isDarkSection ? 'fp-nav-dark' : 'fp-nav-light'}`} data-testid="fullpage-nav">
        {SECTION_CONFIG.map((s, i) => (
          <button
            key={s.id}
            className={`fullpage-dot ${i === activeIndex ? 'active' : ''}`}
            onClick={() => goTo(i)}
            title={s.name}
            data-testid={`nav-dot-${s.id}`}
          />
        ))}
      </nav>
    </div>
  );
}

export default App;
